/** @module @liquidefy/code-editor/layout/layout-widgets @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  Position,
  Range,
  ViewportState,
  ZoneWidget,
  ContentWidget,
  ContentWidgetPosition,
  ContentWidgetPositionPreference,
  OverlayWidget,
  OverlayWidgetPosition,
  OverlayWidgetPositionPreference,
  WidgetId,
  EditOperation,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  asWidgetId,
  generateId,
  clamp,
} from '../code-editor.js';

// ═══ §2 ZONE WIDGETS — inline content areas positioned after specific lines ═══

/**
 * Creates a new zone widget inserted after a specific line.
 */
export function createZoneWidget(
  id: WidgetId,
  afterLine: number,
  heightInLines: number,
  options?: { showInHiddenAreas?: boolean },
): EditorResult<ZoneWidget> {
  if (heightInLines <= 0) {
    return err('INVALID_HEIGHT', 'Zone widget height must be positive');
  }

  const widget: ZoneWidget = {
    id,
    afterLineNumber: afterLine,
    heightInLines,
    showInHiddenAreas: options?.showInHiddenAreas ?? false,
  };

  return ok(widget, 'ZONE_WIDGET_CREATED', `Zone widget ${id} created after line ${afterLine}`);
}

/**
 * Updates an existing zone widget's properties.
 */
export function updateZoneWidget(
  widgets: ZoneWidget[],
  id: WidgetId,
  updates: Partial<Pick<ZoneWidget, 'afterLineNumber' | 'heightInLines' | 'showInHiddenAreas'>>,
): EditorResult<ZoneWidget[]> {
  const index = widgets.findIndex((w) => w.id === id);
  if (index === -1) return err('NOT_FOUND', `Zone widget ${id} not found`);

  const existing = widgets[index]!;
  const updated: ZoneWidget = {
    ...existing,
    afterLineNumber: updates.afterLineNumber ?? existing.afterLineNumber,
    heightInLines: updates.heightInLines ?? existing.heightInLines,
    showInHiddenAreas: updates.showInHiddenAreas ?? existing.showInHiddenAreas,
  };

  const result = [...widgets];
  result[index] = updated;
  return ok(result, 'ZONE_WIDGET_UPDATED', `Zone widget ${id} updated`);
}

/**
 * Removes a zone widget by ID.
 */
export function removeZoneWidget(
  widgets: ZoneWidget[],
  id: WidgetId,
): EditorResult<ZoneWidget[]> {
  const filtered = widgets.filter((w) => w.id !== id);
  if (filtered.length === widgets.length) {
    return err('NOT_FOUND', `Zone widget ${id} not found`);
  }
  return ok(filtered, 'ZONE_WIDGET_REMOVED', `Zone widget ${id} removed`);
}

/**
 * Returns the zone widget positioned at or after the given line, if any.
 */
export function getZoneWidgetAt(
  widgets: ZoneWidget[],
  line: number,
): ZoneWidget | null {
  return widgets.find((w) => w.afterLineNumber === line) ?? null;
}

/**
 * Adjusts zone widget positions after an edit operation changes line numbers.
 * Inserts shift widgets down, deletions shift them up.
 */
export function adjustZoneWidgetsForEdit(
  widgets: ZoneWidget[],
  edit: EditOperation,
): EditorResult<ZoneWidget[]> {
  const startLine = edit.range.start.line;
  const endLine = edit.range.end.line;
  const insertedLines = edit.text.split('\n').length - 1;
  const deletedLines = endLine - startLine;
  const delta = insertedLines - deletedLines;

  const adjusted = widgets.map((w) => {
    if (w.afterLineNumber <= startLine) return w;
    if (w.afterLineNumber <= endLine) {
      // Widget is within the edited range — move to start of edit
      return { ...w, afterLineNumber: startLine };
    }
    // Widget is after the edited range — shift by delta
    return { ...w, afterLineNumber: Math.max(0, w.afterLineNumber + delta) };
  });

  return ok(adjusted, 'ZONE_WIDGETS_ADJUSTED', `Adjusted ${adjusted.length} zone widgets`);
}

/**
 * Sorts zone widgets by their line position (ascending).
 */
export function sortZoneWidgets(widgets: ZoneWidget[]): ZoneWidget[] {
  return [...widgets].sort((a, b) => a.afterLineNumber - b.afterLineNumber);
}

/**
 * Returns the total height occupied by all zone widgets, in lines.
 */
export function totalZoneHeight(widgets: ZoneWidget[]): number {
  let total = 0;
  for (const w of widgets) {
    total += w.heightInLines;
  }
  return total;
}

// ═══ §3 CONTENT WIDGETS — positioned at editor text coordinates ═══

/**
 * Creates a content widget positioned at a specific editor position.
 */
export function createContentWidget(
  id: WidgetId,
  position: Position,
  preference?: ContentWidgetPositionPreference[],
): EditorResult<ContentWidget> {
  const widget: ContentWidget = {
    id,
    position: {
      position,
      preference: preference ?? ['above', 'below'],
    },
    allowEditorOverflow: false,
    suppressMouseDown: false,
  };

  return ok(widget, 'CONTENT_WIDGET_CREATED', `Content widget ${id} created`);
}

/**
 * Updates the position of an existing content widget.
 */
export function updateContentWidgetPosition(
  widgets: ContentWidget[],
  id: WidgetId,
  position: Position,
): EditorResult<ContentWidget[]> {
  const index = widgets.findIndex((w) => w.id === id);
  if (index === -1) return err('NOT_FOUND', `Content widget ${id} not found`);

  const existing = widgets[index]!;
  const updated: ContentWidget = {
    ...existing,
    position: { ...existing.position, position },
  };

  const result = [...widgets];
  result[index] = updated;
  return ok(result, 'CONTENT_WIDGET_MOVED', `Content widget ${id} repositioned`);
}

/**
 * Removes a content widget by ID.
 */
export function removeContentWidget(
  widgets: ContentWidget[],
  id: WidgetId,
): EditorResult<ContentWidget[]> {
  const filtered = widgets.filter((w) => w.id !== id);
  if (filtered.length === widgets.length) {
    return err('NOT_FOUND', `Content widget ${id} not found`);
  }
  return ok(filtered, 'CONTENT_WIDGET_REMOVED', `Content widget ${id} removed`);
}

/**
 * Resolves the actual pixel position for a content widget based on its preference
 * and available space in the viewport.
 */
export function resolveContentWidgetPosition(
  widget: ContentWidget,
  viewport: ViewportState,
  editorWidth: number,
  editorHeight: number,
): EditorResult<{ x: number; y: number; preference: ContentWidgetPositionPreference }> {
  const pos = widget.position.position;
  const baseX = pos.column * viewport.charWidth - viewport.scrollLeft;
  const baseY = (pos.line - viewport.startLine) * viewport.lineHeight;

  // Assume a default widget height of 100px for space calculation
  const widgetHeight = 100;

  for (const pref of widget.position.preference) {
    const resolved = tryContentPosition(pref, baseX, baseY, viewport.lineHeight, widgetHeight, editorHeight);
    if (resolved !== null) {
      return ok(
        { x: clamp(resolved.x, 0, editorWidth), y: resolved.y, preference: pref },
        'CONTENT_POSITION_RESOLVED',
        `Resolved to ${pref}`,
      );
    }
  }

  // Fallback to exact position
  return ok(
    { x: clamp(baseX, 0, editorWidth), y: baseY, preference: 'exact' },
    'CONTENT_POSITION_FALLBACK',
    'Used fallback exact position',
  );
}

/**
 * Tests whether a content widget fits at the given position preference.
 */
export function contentWidgetFits(
  widget: ContentWidget,
  position: ContentWidgetPositionPreference,
  viewport: ViewportState,
): boolean {
  const pos = widget.position.position;
  const baseY = (pos.line - viewport.startLine) * viewport.lineHeight;

  switch (position) {
    case 'above':
      return baseY >= 100; // enough room above
    case 'below':
      return baseY + viewport.lineHeight + 100 <= viewport.height;
    case 'exact':
      return true;
  }
}

/**
 * Finds the best position for a content widget among its preferences.
 */
export function bestContentWidgetPosition(
  widget: ContentWidget,
  viewport: ViewportState,
): ContentWidgetPositionPreference {
  for (const pref of widget.position.preference) {
    if (contentWidgetFits(widget, pref, viewport)) return pref;
  }
  return 'exact';
}

// ═══ §4 OVERLAY WIDGETS — floating UI panels over the editor ═══

/**
 * Creates an overlay widget with a position preference.
 */
export function createOverlayWidget(
  id: WidgetId,
  position: OverlayWidgetPositionPreference,
): EditorResult<OverlayWidget> {
  const widget: OverlayWidget = {
    id,
    position: { preference: position },
  };

  return ok(widget, 'OVERLAY_WIDGET_CREATED', `Overlay widget ${id} created`);
}

/**
 * Updates the position preference of an overlay widget.
 */
export function updateOverlayPosition(
  widgets: OverlayWidget[],
  id: WidgetId,
  position: OverlayWidgetPositionPreference,
): EditorResult<OverlayWidget[]> {
  const index = widgets.findIndex((w) => w.id === id);
  if (index === -1) return err('NOT_FOUND', `Overlay widget ${id} not found`);

  const result = [...widgets];
  result[index] = { ...widgets[index]!, position: { preference: position } };
  return ok(result, 'OVERLAY_POSITION_UPDATED', `Overlay widget ${id} repositioned`);
}

/**
 * Removes an overlay widget by ID.
 */
export function removeOverlayWidget(
  widgets: OverlayWidget[],
  id: WidgetId,
): EditorResult<OverlayWidget[]> {
  const filtered = widgets.filter((w) => w.id !== id);
  if (filtered.length === widgets.length) {
    return err('NOT_FOUND', `Overlay widget ${id} not found`);
  }
  return ok(filtered, 'OVERLAY_WIDGET_REMOVED', `Overlay widget ${id} removed`);
}

/**
 * Computes the pixel position for an overlay widget based on its preference
 * and the editor dimensions.
 */
export function resolveOverlayPosition(
  widget: OverlayWidget,
  editorWidth: number,
  editorHeight: number,
): EditorResult<{ x: number; y: number }> {
  const padding = 10;

  switch (widget.position.preference) {
    case 'top-right-corner':
      return ok(
        { x: editorWidth - padding, y: padding },
        'OVERLAY_RESOLVED',
        'Positioned at top-right corner',
      );
    case 'bottom-right-corner':
      return ok(
        { x: editorWidth - padding, y: editorHeight - padding },
        'OVERLAY_RESOLVED',
        'Positioned at bottom-right corner',
      );
    case 'top-center':
      return ok(
        { x: Math.floor(editorWidth / 2), y: padding },
        'OVERLAY_RESOLVED',
        'Positioned at top-center',
      );
    default:
      return ok({ x: 0, y: 0 }, 'OVERLAY_DEFAULT', 'Default position');
  }
}

// ═══ §5 WIDGET LIFECYCLE — show, hide, visibility queries, counting ═══

/**
 * All widget types share a common id field. This type covers the union.
 */
type AnyWidget = ZoneWidget | ContentWidget | OverlayWidget;

/**
 * Widget visibility state tracked externally from widget data.
 * Maps widget ID to visibility boolean.
 */
type WidgetVisibility = Map<string, boolean>;

/**
 * Sets a widget as visible.
 */
export function showWidget(
  visibility: WidgetVisibility,
  id: WidgetId,
): WidgetVisibility {
  const next = new Map(visibility);
  next.set(id as string, true);
  return next;
}

/**
 * Sets a widget as hidden.
 */
export function hideWidget(
  visibility: WidgetVisibility,
  id: WidgetId,
): WidgetVisibility {
  const next = new Map(visibility);
  next.set(id as string, false);
  return next;
}

/**
 * Returns whether a widget is currently visible.
 * Defaults to true if not explicitly tracked.
 */
export function isWidgetVisible(
  visibility: WidgetVisibility,
  id: WidgetId,
): boolean {
  return visibility.get(id as string) ?? true;
}

/**
 * Returns all widgets that are visible and within the given viewport.
 * Zone widgets are checked by their afterLineNumber against the viewport range.
 */
export function getVisibleWidgets(
  widgets: {
    zone: ZoneWidget[];
    content: ContentWidget[];
    overlay: OverlayWidget[];
  },
  viewport: ViewportState,
): {
  zone: ZoneWidget[];
  content: ContentWidget[];
  overlay: OverlayWidget[];
} {
  const zone = widgets.zone.filter(
    (w) => w.afterLineNumber >= viewport.startLine && w.afterLineNumber < viewport.endLine,
  );

  const content = widgets.content.filter(
    (w) => {
      const line = w.position.position.line;
      return line >= viewport.startLine && line < viewport.endLine;
    },
  );

  // Overlay widgets are always visible (not position-dependent)
  const overlay = [...widgets.overlay];

  return { zone, content, overlay };
}

/**
 * Returns the total count of all widgets.
 */
export function widgetCount(widgets: {
  zone: ZoneWidget[];
  content: ContentWidget[];
  overlay: OverlayWidget[];
}): number {
  return widgets.zone.length + widgets.content.length + widgets.overlay.length;
}

/**
 * Returns the count of widgets broken down by type.
 */
export function widgetsByType(widgets: {
  zone: ZoneWidget[];
  content: ContentWidget[];
  overlay: OverlayWidget[];
}): { zone: number; content: number; overlay: number } {
  return {
    zone: widgets.zone.length,
    content: widgets.content.length,
    overlay: widgets.overlay.length,
  };
}

// ═══ INTERNAL HELPERS ═══

/**
 * Attempts to resolve a content widget position for a specific preference.
 * Returns null if the position would clip outside the editor bounds.
 */
function tryContentPosition(
  preference: ContentWidgetPositionPreference,
  baseX: number,
  baseY: number,
  lineHeight: number,
  widgetHeight: number,
  editorHeight: number,
): { x: number; y: number } | null {
  switch (preference) {
    case 'above': {
      const y = baseY - widgetHeight;
      if (y < 0) return null;
      return { x: baseX, y };
    }
    case 'below': {
      const y = baseY + lineHeight;
      if (y + widgetHeight > editorHeight) return null;
      return { x: baseX, y };
    }
    case 'exact':
      return { x: baseX, y: baseY };
    default:
      return null;
  }
}
